import Client from "../Client";
import Auth from "../../Auth";

const HasClientTraits = WrappedClass => {
    return class HasClientTraits extends WrappedClass {

        /*
        |--------------------------------------------------------------------------
        | Client User Helper Methods
        |--------------------------------------------------------------------------
        */
        isClientManager() {
            return this.type === laravel.user_types.client_manager;
        }

        isClientConsultant() {
            return this.type === laravel.user_types.client_consultant;
        }

        hasMultipleClientOrgs()
        {
            if (this.isClient() && this.has_multiple_client_organizations) {
                return true;
            }

            if (!this.isClientConsultant()) {
                return false;
            }

            return TB.isArray(this.assigned_clients);
        }

        allClientOrgs() {
            if (!this.hasMultipleClientOrgs()) {
                return false;
            }

            return Client.makeMany(
                this.assigned_clients || this.client_organizations
                );
        }

        isClient() {
            return this.isClientManager()
                || this.isClientConsultant()
                || this.type === laravel.user_types.client;
        }

        hasClientOrg() {
            return TB.isObject(this.client_organization);
        }

        hasRegisteredClientOrg() {
            return this.hasClientOrg()
                && this.clientOrg().isRegistered();
        }

        clientOrg() {
            if (!this.isClient() && !this.canManageClients()) {
                return;
            }

            if (!TB.isObject(this.client_organization)) {
                return;
            }

            return Client.make(this.client_organization);
        }

        hasSupplierClientOrg() {
            let client = this.clientOrg();
            if (!client) {
                return false;
            }

            return client.is_supplier;
        }

        supplierOrg = () => {
            if(!this.canManageClientResourceTalent() && !this.isTalentSupplierResource()) {
                return false;
            }
            let client;

            if(this.canManageClientResourceTalent()) {
                return this.clientOrg();
            }else if(this.isTalentSupplierResource()) {
                client = Client.make({...this.supplier_client})
            }
            return {
                id: client.id,
                name: client.name,
                type: client.type,
                logo_path: client.logo_path,
                banner_image_path: client.banner_image_path,
                size: client.size,
                website: client.website,
                about: client.about,
                offers_service_work: client.offers_service_work,
                offers_byo_work: client.offers_service_work
            }
        }

        clientIndustry() {
            if (!this.clientOrg()
                || !TB.isObject(this.clientOrg().industry)) {
                return;
            }

            return {
                ...this.clientOrg().industry,
                importance: TB.invert(laravel.importance_levels || [])['Important']
            };
        }

        clientRole() {
            if (!this.isClient()) {
                return false;
            }

            let role = this.role_name || this.clientOrg()?.role;

            return role;
        }

        isClientOwner() {
            return this.clientRole() === Client.Roles.Owner;
        }

        isClientSuperAdmin() {
            if (TB.isDefined(this.is_client_super_admin)) {
                return this.is_client_super_admin;
            }

            return this.clientRole() === Client.Roles.SuperAdmin
                || this.clientRole() === Client.Roles.Owner
        }

        isClientAdmin() {
            if (TB.isDefined(this.is_client_admin) || TB.isDefined(this.is_client_super_admin)) {
                return this.is_client_admin || this.is_client_super_admin;
            }

            return this.clientRole() === Client.Roles.Admin
                || this.clientRole() === Client.Roles.SuperAdmin
                || this.clientRole() === Client.Roles.Owner;
        }

        isClientBaseUser() {
            return this.clientRole() === Client.Roles.BaseUser;
        }

        canManageClientResourceTalent() {
            if (this.canManageTalent()) {
                return true;
            }
            if (!this.hasSupplierClientOrg()) {
                return false
            }
            return this.can_manage_resource_talent ||
                this.isClientConsultant() ||
                this.isClientSuperAdmin();
        }

        invitableUserRoles()
        {
            if (!this.isClient()) {
                return false;
            }
            if (this.isClientBaseUser()) {
                return false;
            }

            let lookup = TB.invert(laravel.client_user_roles);
            // if (!this.canManageClientResourceTalent()) {
                delete lookup[Client.Roles.TalentResource];
            // }

            if(this.isClientOwner()
                || this.isClientConsultant()
                || this.isClientSuperAdmin())
            {
                return TB.invert(lookup);
            }

            delete lookup[Client.Roles.SuperAdmin];

            if (!this.isClientAdmin()) {
                delete lookup[Client.Roles.Admin];
            }

            if (TB.keys(lookup).length <= 1) {
                return false;
            }

            return TB.invert(lookup);
        }

        manageableUserRoles()
        {
            if (!this.isClient()) {
                return false;
            }
            if (this.isClientBaseUser()) {
                return false;
            }

            let roles = TB.invert(laravel.client_user_roles);
            delete roles[Client.Roles.TalentResource];

            if(this.isClientOwner() || this.isClientConsultant()) {

                return TB.invert(roles);
            }

            delete roles[Client.Roles.SuperAdmin];

            if (!this.isClientSuperAdmin()) {
                delete roles[Client.Roles.Admin];
            }

            // if (!this.isClientSuperAdmin() && !this.isClientAdmin()) {
            //     delete lookup[Client.Roles.Admin];
            // }

            if (TB.keys(roles).length <= 1) {
                return false;
            }

            return TB.invert(roles);
        }

        canRemoveUserToGroup(user) {
            if (this.canManageClients() || this.isClientConsultant() || this.isClientSuperAdmin()) {
                return true;
            }
            return !!(this.isClientAdmin() && !user.isClientAdmin());
        }

        canEditUserGroupPermissions(user) {
            if (user.isClientSuperAdmin()
                || user.isClientAdmin()) {
                return false;
            }

            if (this.canManageClients()
                || this.isClientConsultant()
                || this.isClientSuperAdmin()) {
                return true;
            }

            if (user.isClientBaseUser()
                && this.isClientAdmin()) {
                return true;
            }

            return false;
        }

        canEditGroupPermissions() {
            return this.canManageClients()
                || this.isClientConsultant()
                || this.isClientSuperAdmin()
                || this.isClientAdmin()
        }

        canManageGroups() {
            return this.canManageClients()
                || this.isClientConsultant()
                || this.isClientSuperAdmin()
        }
        canAddCollaboratorToGroupProject(groupId) {
            if (this.is_consultant || this.canManageClients() || this.isClientConsultant() || this.isClientSuperAdmin()) {
                return true;
            }
            return TB.find(this.client_project_groups || [], ['id', groupId])
        }
        /*
        |--------------------------------------------------------------------------
        | Permissions
        |--------------------------------------------------------------------------
        */


        hasProjectGroupPermission(project, permission)
        {
            if (this.canManageClients() || this.isClientConsultant() || this.isClientAdmin()) {
                return true;
            }

            if (!this.isClient()) return false;

            if (!project?.clientProjectGroup()) {
                return true;
            }

            let group = project.clientProjectGroup();

            let permissions = TB.find(this.client_project_groups || [], ['id', group.id]);

            if (!permissions) return false;

            return permissions[permission]
        }
        // Project Management
        // can_view_brief
        // can_create_project
        // can_add_remove_positions
        // can_edit_project_and_positions
        //

        // done
        canViewProjectBrief(project) {
            return this.hasProjectGroupPermission(
                project,
                'can_view_brief'
            )
        }

        // done
        canCreateProjectBrief(project) {
            return this.canViewProjectBrief(project) &&
                this.hasProjectGroupPermission(
                    project,
                    'can_create_project'
                )
        }

        // done
        canAddRemoveProjectPositions(project) {
            if (project.isFixedFee() && project.hasConfirmedProjectScope()) {
                return false;
            }
            return this.canViewProjectBrief(project) &&
                this.hasProjectGroupPermission(
                    project,
                    'can_add_remove_positions'
                )
        }

        // done
        canEditProjectAndPositions(project) {
            return this.canViewProjectBrief(project) &&
                this.hasProjectGroupPermission(
                    project,
                    'can_edit_project_and_positions'
                )
        }

        checkAllowedToCreateProjectGroups() {
            const user = TB.auth().user();

            if (!user) {
                return [{name: 'default'}];
            }
            return TB.filter(user.client_project_groups || [], group => !!group.can_create_project);
        }



        // can_view_talent
        // can_invite_talent
        // can_request_interview
        // can_assign_talent
        //

        canViewTalentForProject(project) {
            return this.hasProjectGroupPermission(
                project,
                'can_view_talent'
            )
        }

        // done
        canInviteTalentForProject(project) {
            return this.canViewTalentForProject(project) &&
                this.hasProjectGroupPermission(
                    project,
                    'can_invite_talent'
                )
        }

        // done
        canRequestTalentInterviewForProject(project) {
            return this.canViewTalentForProject(project) &&
                this.hasProjectGroupPermission(
                    project,
                    'can_request_interview'
                )
        }

        // done
        canAssignTalentForProject(project) {
            return this.canViewTalentForProject(project) &&
                this.hasProjectGroupPermission(
                    project,
                    'can_assign_talent'
                )
        }
        // can_view_sow
        // can_request_sow
        // can_edit_sow
        // can_complete_sow

        canViewSOWForProject(project) {
            return this.hasProjectGroupPermission(
                project,
                'can_view_sow'
            )
        }

        canRequestSOWForProject(project) {
            return this.canViewSOWForProject(project) &&
                this.hasProjectGroupPermission(
                    project,
                    'can_request_sow'
                )
        }

        canEditSOWForProject(project) {
            return this.canViewSOWForProject(project) &&
                this.hasProjectGroupPermission(
                    project,
                    'can_edit_sow'
                )
        }

        canCompleteSOWForProject(project) {
            return this.canViewSOWForProject(project) &&
                this.hasProjectGroupPermission(
                    project,
                    'can_complete_sow'
                )
        }


        /*
        |--------------------------------------------------------------------------
        | Network Requests
        |--------------------------------------------------------------------------
        */

        static FetchAllForClientRequest;
        static DeactivateForClientRequest;
        static RestoreForClientRequest;
        static ResendInviteForClientRequest;

        static async fetchAllForClient(requestOptions = {}, queryParams = '') {
            let user = TB.auth().user();
            if (!user
                || !user.hasRegisteredClientOrg()) {
                return;
            }

            this.FetchAllForClientRequest = TB.request(
                TB.api_endpoint(`/api/clients/${user.clientOrg().id}/users${queryParams}`)
            );

            let response = await this.FetchAllForClientRequest.get(requestOptions).catch(console.warn);

            if (!TB.has(response, 'success.data.users')) {
                return;
            }

            return this.makeMany(
                response.success.data.users
            );
        }

        static async fetchAllForTalentManager(params = {}, refresh = true) {
            let user = Auth.registeredTalentUser();
            if (!user || !user.canManageTalent()) {
                return;
            }

            if (this.Cached && this.Cached.length && !refresh) {
                return this.Cached;
            }

            let formattedParams = {};
            for (let [key, value] of Object.entries(params)) {
                if (TB.isArray(value)) {
                    if (!value.length) {
                        continue;
                    }
                    value = value.join(',');
                }
                formattedParams[key] = value;
            }

            await this.cancelRequest('FetchAllForTalentManagerRequest')
            // console.log(formattedParams);
            this.FetchAllForTalentManagerRequest = TB.request(
                TB.api_endpoint(`/api/users/talent`),
                formattedParams
            );

            let Request = this.FetchAllForTalentManagerRequest;

            let response = await Request.get().catch(console.warn);

            if (Request.wasCancelled) {
                return;
            }

            if (!TB.has(response, 'success.data.users')) {
                return TB.event("Talent@FetchForTalentManagerFailed", Request)
            }

            let {
                users,
                current_page,
                last_page,
                total
            } = response.success.data;

            let models = this.makeMany([...users]);

            // let statusOptions = User.statusOptions();

            // talent = TB.orderBy(
            //     talent,
            //     user => statusOptions.indexOf(user.display_status),
            //     'asc'
            // );

            if (params.page && params.page > 1 &&
                (this.CachedTalent?.length + models.length) === (params.per_page * params.page)) {
                models = [
                    ...this.CachedTalent,
                    ...models
                ];
            }

            this.CachedTalent = models;

            TB.event("Talent@FetchForTalentManagerUpdated",
                {
                    models,
                    current_page,
                    last_page,
                    total
                }
            )
        }


        static async cancelSearchKeywords()
        {
            if (this.SearchKeywordsRequest && !this.SearchKeywordsRequest.didFinish) {
                await this.SearchKeywordsRequest.cancel();
            }
        }

        static async searchKeywords(query)
        {
            await this.cancelSearchKeywords();

            let user = Auth.registeredTalentUser();
            if (!user || !user.canManageTalent()) {
                return;
            }

            this.SearchKeywordsRequest = TB.request(
                TB.api_endpoint(`/api/users/keywords`), {
                    search: query
                });

            let response = await this.SearchKeywordsRequest.get().catch(console.warn);

            if (!TB.has(response, 'success.data.results'))
            {
                if (this.SearchKeywordsRequest.wasCancelled) {
                    return [];
                }

                let didHandleError = await this.SearchKeywordsRequest.handleError();
                if (didHandleError
                    && this.SearchKeywordsRequest.shouldRerun) {
                    return this.searchKeywords(query);
                }

                return false
            }

            return [...response.success.data.results];
        }


        static async fetchForTalentAsManager(talentId)
        {
            let user = Auth.registeredTalentUser();
            if (!user || !user.canManageTalent()) {
                return;
            }
            this.FetchForTalentAsManagerRequest = TB.request(
                TB.api_endpoint(`/api/users/talent/${talentId}`)
            );

            let response = await this.FetchForTalentAsManagerRequest.get().catch(console.warn);

            if (!TB.has(response, 'success.data.user')) {
                return;
            }

            let talent = this.make({...response.success.data.user})

            return talent
        }


        static async fetchForClient(userId)
        {
            let users = await this.fetchAllForClient();
            if (!users) {
                return;
            }

            let lookup = TB.keyBy(users, 'id');

            let user = lookup[userId];
            if (!user) {
                this.FetchAllForClientRequest.response.status = 404;
                this.FetchAllForClientRequest.response.data.error = {
                    message: 'not_found',
                };
            }
            else {
                return this.make({...user});
            }
        }

        static async fetchTalentSupplierResource(talentId, requestOptions = {})
        {
            let user = Auth.registeredClientUser();
            if (!user || !user.hasSupplierClientOrg() || !user.canManageClientResourceTalent()) {
                throw new Error("Unauthorized");
            }
            const clientOrg = user.clientOrg()
            this.TalentSupplierResource = TB.request(
                TB.api_endpoint(`/api/clients/${clientOrg.id}/users/${talentId}`)
            );

            let response = await this.TalentSupplierResource.get(requestOptions).catch(console.warn);

            if (!TB.has(response, 'success.data.user')) {
                return false;
            }

            return this.make({...response.success.data.user, supplier_client: {...clientOrg}});
        }

        async deactivate()
        {
            let user = TB.auth().user();
            if (!user
                || !user.hasRegisteredClientOrg()) {
                return;
            }

            this.constructor.DeactivateForClientRequest = TB.request(
                TB.api_endpoint(`/api/clients/${user.clientOrg().id}/users/${this.id}/delete`)
            );

            let response = await this.constructor.DeactivateForClientRequest.post().catch(console.warn);

            if (!TB.has(response, 'success.data.user')) {
                return;
            }

            let updatedUser =  this.constructor.make(
                response.success.data.user
            );

            await this.constructor.refreshCacheFor(updatedUser)

            return updatedUser;
        }

        async restore()
        {
            let user = TB.auth().user();
            if (!user
                || !user.hasRegisteredClientOrg()) {
                return;
            }

            this.constructor.RestoreForClientRequest = TB.request(
                TB.api_endpoint(`/api/clients/${user.clientOrg().id}/users/${this.id}/restore`)
            );

            let response = await this.constructor.RestoreForClientRequest.post().catch(console.warn);

            if (!TB.has(response, 'success.data.user')) {
                return;
            }

            let updatedUser = this.constructor.make(
                response.success.data.user
            );

            await this.constructor.refreshCacheFor(updatedUser)

            return updatedUser;
        }

        async resendInvite() {
            let user = TB.auth().user();
            if (!user
                || !user.hasRegisteredClientOrg()) {
                return;
            }

            this.constructor.ResendInviteForClientRequest = TB.request(
                TB.api_endpoint(`/api/clients/${user.clientOrg().id}/users/${this.id}/reinvite`)
            );

            let response = await this.constructor.ResendInviteForClientRequest.post().catch(console.warn);

            if (!TB.has(response, 'success.data.user')) {
                return;
            }

            let updatedUser = this.constructor.make(
                response.success.data.user
            );

            await this.constructor.refreshCacheFor(updatedUser)

            return updatedUser;
        }
    }
}


export default HasClientTraits